import { createFileRoute, Link } from "@tanstack/react-router";
import { EsLiveRateSnapshot } from "@/components/rates/EsLiveRateSnapshot";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, FAQ, CTABand, StatRow } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";
import heroAsset from "@/assets/hero-dscr-investment-property.jpg.asset.json";

const faqItems = [
  { q: "¿Qué es un préstamo DSCR?", a: "Es un préstamo para propiedades de inversión en el que la calificación se basa en el flujo de caja de la propiedad (renta dividida por PITIA), no en ingresos personales como W-2s o declaraciones de impuestos." },
  { q: "¿Hasta qué LTV se puede llegar?", a: "Hasta 80% LTV en propiedades elegibles, sujeto a crédito, reservas, DSCR, tipo de propiedad y aprobación de suscripción." },
  { q: "¿Puedo cerrar en LLC?", a: "Sí. LLC, LP, S-Corp, C-Corp y Series LLC son comúnmente aceptadas en programas DSCR con fines comerciales." },
  { q: "¿Qué propiedades califican?", a: "Propiedades de inversión residenciales de 1 a 8 unidades, townhomes, condominios aprobados y propiedades STR/ Airbnb en ubicaciones elegibles." },
  { q: "¿Se requieren ingresos personales?", a: "No en los programas DSCR estándar. Se requiere crédito del prestatario/entidad y reservas verificables." },
];

export const Route = createFileRoute("/es/prestamos-dscr/")({
  head: () =>
    buildHead({
      title: "Préstamos DSCR — Califique con la Renta, No con Impuestos",
      description:
        "Préstamos DSCR para propiedades de inversión de 1 a 8 unidades. Hasta 80% LTV, sin ingresos personales. LLC-friendly. Revisión de escenario el mismo día hábil.",
      url: "/es/prestamos-dscr",
      locale: "es",
      keywords: "préstamos DSCR, préstamos calificación renta, préstamos sin declaración impuestos, préstamos LLC, DSCR LTV 80, préstamos propiedad alquiler",
      image: heroAsset.url,
      breadcrumbs: [
        { name: "Inicio", url: "/es" },
        { name: "Préstamos DSCR", url: "/es/prestamos-dscr" },
      ],
      service: { name: "Préstamos DSCR para propiedades de inversión" },
      faqs: faqItems,
    }),
  component: DscrPage,
});

function DscrPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Préstamos DSCR"
        title="Préstamos DSCR: califique con la renta de la propiedad, no con sus impuestos."
        subtitle="Hasta 80% LTV, 1–8 unidades, LLC-friendly, sin W-2s ni declaraciones de impuestos personales. Cotización y aprobación tras la suscripción del prestamista mayorista."
        image={{ src: heroAsset.url, alt: "Propiedad de inversión financiada con DSCR" }}
      />
      <Section>
        <SectionHeader title="Puntos clave del programa" center />
        <StatRow
          items={[
            { label: "Max LTV", value: "80%" },
            { label: "Unidades", value: "1–8" },
            { label: "DSCR mín.", value: "0.75" },
            { label: "Monto máx.", value: "$5M" },
            { label: "Documentos ingresos", value: "No" },
            { label: "Vestimenta", value: "LLC / Corp" },
          ]}
        />
        <p className="mt-3 text-xs text-muted-foreground">
          Los máximos mostrados son límites de programa en préstamos DSCR elegibles con fines comerciales. Los términos reales dependen del crédito, propiedad, reservas, avalúo y aprobación de suscripción.
        </p>
      </Section>
      <Section>
        <SectionHeader title="¿Por qué DSCR?" center />
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold text-foreground">Sin ingresos personales</h3>
            <p className="mt-2 text-sm text-muted-foreground">La calificación se basa en la renta que genera la propiedad, no en su empleo ni declaraciones fiscales.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold text-foreground">Estructura flexible</h3>
            <p className="mt-2 text-sm text-muted-foreground">Fijo a 30 años, solo intereses, cash-out, refinanciamiento y HELOC de inversión en programas elegibles.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold text-foreground">Cierre en entidad</h3>
            <p className="mt-2 text-sm text-muted-foreground">Cierre en nombre de LLC, LP o corporación sin prima por usar entidad en programas elegibles.</p>
          </div>
        </div>
      </Section>
      <Section>
        <EsLiveRateSnapshot heading="Tasas actuales de préstamos DSCR para propiedades de alquiler" intro="Así se ve hoy el precio de un préstamo DSCR a 30 años en un escenario de inversión de ejemplo. Su tasa real dependerá del DSCR, LTV, crédito y propiedad." />
      </Section>
      <Section>
        <div className="mx-auto max-w-4xl">
          <SectionHeader title="Preguntas frecuentes" center />
          <FAQ items={faqItems} />
        </div>
      </Section>
      <Section>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/es/calculadora-dscr" className="rounded-md bg-brand px-5 py-3 font-semibold text-primary-foreground hover:bg-brand-dark">Calculadora DSCR</Link>
          <Link to="/es/tasas-prestamos-dscr" className="rounded-md border border-brand px-5 py-3 font-semibold text-brand hover:bg-brand/10">Ver tasas DSCR</Link>
          <Link to="/es/requisitos-prestamo-dscr" className="rounded-md border border-brand px-5 py-3 font-semibold text-brand hover:bg-brand/10">Requisitos</Link>
        </div>
      </Section>
      <CTABand title="¿Listo para revisar su escenario DSCR?" body="Envíenos su escenario y un oficial de préstamos le responderá." />
    </SiteShell>
  );
}
